import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";
import { countUp, withGroupSpaces } from "~/lib/web/counter";
import { Clock, fitStage, whileInView } from "~/lib/web/loop";

const STAGE_WIDTH = 440;

/**
 * The canvas is 440 wide and its widest panel is 404, so the last 36px hold
 * nothing but the cursor. Cropping to this scale removes that margin before it
 * removes any of the panel's own shadow.
 */
const MIN_SCALE = 0.8;

const STAGE_HEIGHT = 248;
const PILL_HEIGHT = 44;
const LIST_SIZE = { width: 300, height: 136 };
const BUDGET_SIZE = { width: 404, height: 158 };
const PANEL_TOP = 62;

/**
 * Where the budget is positioned on the track at each end, as a fraction. The
 * start is below the projected bill and the end above it, so the drag is what
 * resolves the alert.
 */
const FILL_START = 0.46;
const FILL_END = 0.78;

/**
 * A press is down, held, then released. The beats after a click are expressed
 * as the click plus these, so nothing starts before the press it follows has
 * been released.
 */
const PRESS_MS = 440;
const PRESS_DOWN_MS = 110;

const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const SPRING = "cubic-bezier(0.34, 1.4, 0.64, 1)";

/** Offset from the cursor graphic's own origin to the point of its arrow. */
const CURSOR_TIP_X = 3;
const CURSOR_TIP_Y = 2;

export default class WebAlertController extends Controller<HTMLElement> {
  /** The two ends of the budget, rendered by deck_cards.py alongside the figures. */
  static override values = { budgetFrom: Number, budgetTo: Number };

  declare readonly budgetFromValue: number;
  declare readonly budgetToValue: number;

  static override targets = [
    "stage",
    "pill",
    "panel",
    "list",
    "budget",
    "label",
    "icon",
    "amount",
    "fill",
    "green",
    "cap",
    "cursor",
    "picked",
  ];
  declare readonly stageTarget: HTMLElement;
  declare readonly hasStageTarget: boolean;
  declare readonly pillTarget: HTMLElement;
  declare readonly hasPillTarget: boolean;
  declare readonly panelTarget: HTMLElement;
  declare readonly hasPanelTarget: boolean;
  declare readonly listTarget: HTMLElement;
  declare readonly budgetTarget: HTMLElement;
  declare readonly labelTargets: HTMLElement[];
  declare readonly iconTargets: HTMLElement[];
  declare readonly amountTarget: HTMLElement;
  declare readonly fillTarget: HTMLElement;
  declare readonly hasFillTarget: boolean;
  declare readonly greenTarget: HTMLElement;
  declare readonly capTarget: HTMLElement;
  declare readonly cursorTarget: HTMLElement;
  declare readonly pickedTarget: HTMLElement;

  private clock = new Clock();
  private stop: (() => void) | null = null;
  private unfit: (() => void) | null = null;
  private cancelCount: (() => void) | null = null;

  override connect(): void {
    if (
      !this.hasStageTarget ||
      !this.hasPillTarget ||
      !this.hasPanelTarget ||
      !this.hasFillTarget
    ) {
      return;
    }
    this.unfit = fitStage(
      this.element,
      this.stageTarget,
      STAGE_WIDTH,
      MIN_SCALE,
    );

    if (prefersReducedMotion()) {
      this.showResolved();
      return;
    }
    this.stop = whileInView(
      this.element,
      () => {
        this.settle();
        this.run();
      },
      () => this.clock.clear(),
    );
  }

  override disconnect(): void {
    this.stop?.();
    this.stop = null;
    this.unfit?.();
    this.unfit = null;
    this.cancelCount?.();
    this.cancelCount = null;
    this.clock.clear();
  }

  private get track(): HTMLElement {
    return this.fillTarget.parentElement ?? this.fillTarget;
  }

  private state(index: number): void {
    this.labelTargets.forEach((label, i) => {
      label.classList.toggle("is-on", i === index);
      this.iconTargets[i]?.classList.toggle("is-on", i === index);
    });
    // The pill sizes to whichever label is showing, since they are stacked
    // absolutely and none of them contributes a content width.
    const label = this.labelTargets[index];
    if (label !== undefined) {
      this.pillTarget.style.width = `${Math.ceil(label.offsetWidth) + 55}px`;
    }
  }

  private showPanel(
    size: { width: number; height: number },
    which: "list" | "budget",
  ): void {
    this.panelTarget.style.opacity = "1";
    this.panelTarget.style.width = `${size.width}px`;
    this.panelTarget.style.height = `${size.height}px`;
    this.listTarget.classList.toggle("is-on", which === "list");
    this.budgetTarget.classList.toggle("is-on", which === "budget");
  }

  private setFill(fraction: number): void {
    this.fillTarget.style.width = `${fraction * (BUDGET_SIZE.width - 28)}px`;
  }

  private moveCursor(x: number, y: number, duration: number): void {
    this.cursorTarget.style.transition = `transform ${duration}ms ${EASE}, opacity 200ms ease`;
    this.cursorTarget.style.transform = `translate(${x - CURSOR_TIP_X}px,${y - CURSOR_TIP_Y}px)`;
  }

  /**
   * The stage is scaled down to fit its column, so a measured rect includes
   * that factor and has to be divided back out before it can be written as a
   * translate inside the stage's own coordinate space.
   */
  private cursorTo(
    target: HTMLElement,
    fractionX: number,
    fractionY: number,
    duration: number,
  ): void {
    const stage = this.stageTarget.getBoundingClientRect();
    const scale = stage.width / this.stageTarget.offsetWidth || 1;
    const rect = target.getBoundingClientRect();
    this.moveCursor(
      (rect.left - stage.left) / scale + (rect.width / scale) * fractionX,
      (rect.top - stage.top) / scale + (rect.height / scale) * fractionY,
      duration,
    );
  }

  private settle(): void {
    const panel = this.panelTarget;
    const pill = this.pillTarget;
    const cursor = this.cursorTarget;

    panel.style.transition = "none";
    panel.style.opacity = "0";
    panel.style.top = `${PANEL_TOP}px`;
    pill.style.transition = "none";
    pill.style.top = `${Math.round((STAGE_HEIGHT - PILL_HEIGHT) / 2)}px`;
    pill.classList.remove("is-pressed");
    this.state(0);
    this.listTarget.classList.remove("is-on");
    this.budgetTarget.classList.remove("is-on");
    this.amountTarget.textContent = withGroupSpaces(this.budgetFromValue);
    this.setFill(FILL_START);
    this.greenTarget.style.opacity = "0";
    this.capTarget.classList.remove("is-on");
    this.pickedTarget.classList.remove("is-hit", "is-sel");
    cursor.style.transition = "none";
    cursor.style.opacity = "0";
    cursor.classList.remove("is-down");
    // Positioned off the stage's bottom right, so the cursor's first move is
    // from outside the stage rather than a fade-in over the pill.
    cursor.style.transform = `translate(${STAGE_WIDTH + 20}px,${STAGE_HEIGHT + 20}px)`;
    void this.stageTarget.offsetWidth;
    pill.style.transition = `top 620ms ${SPRING}, width 400ms ease, transform 220ms ${EASE}`;
    panel.style.transition = `height 520ms ${SPRING}, width 520ms ${SPRING}, opacity 300ms ease`;
  }

  /**
   * The resolved frame. The projected chip and the closing line are the two
   * elements that still show the alert.
   */
  private showResolved(): void {
    this.settle();
    this.pillTarget.style.top = "0px";
    this.state(2);
    this.showPanel(BUDGET_SIZE, "budget");
    this.amountTarget.textContent = withGroupSpaces(this.budgetToValue);
    this.setFill(FILL_END);
    this.greenTarget.style.opacity = "1";
    this.capTarget.classList.add("is-on");
  }

  private tap(): void {
    this.cursorTarget.classList.add("is-down");
    this.clock.at(PRESS_DOWN_MS, () =>
      this.cursorTarget.classList.remove("is-down"),
    );
  }

  /**
   * Every beat is its own click plus the duration of the gesture it depends on,
   * so no beat starts before the press preceding it has been released. The
   * cursor opens the alert, selects the tool, then moves to the track.
   */
  private run(): void {
    // The cursor enters from off stage and moves to the pill.
    this.clock.at(500, () => {
      this.cursorTarget.style.opacity = "1";
      this.cursorTo(this.pillTarget, 0.5, 0.62, 520);
    });
    // 80ms of settle, so the click is not in the frame the pointer lands on.
    this.clock.at(1100, () => {
      this.tap();
      this.pillTarget.classList.add("is-pressed");
      this.clock.at(PRESS_MS, () =>
        this.pillTarget.classList.remove("is-pressed"),
      );
    });

    // The pill moves to the corner and the list opens under it.
    this.clock.at(1600, () => {
      this.pillTarget.style.top = "0px";
      this.state(1);
      this.showPanel(LIST_SIZE, "list");
    });

    // The cursor sets off only once the rows are up, so the flagged row and its
    // sparkline can be read first.
    this.clock.at(2200, () => this.cursorTo(this.pickedTarget, 0.28, 0.5, 900));
    this.clock.at(3180, () => {
      this.tap();
      // Applied at the bottom of the press, not the top, or the row reacts
      // before the button is down.
      this.clock.at(PRESS_DOWN_MS, () =>
        this.pickedTarget.classList.add("is-hit"),
      );
      this.clock.at(PRESS_DOWN_MS + 130, () => {
        this.pickedTarget.classList.remove("is-hit");
        this.pickedTarget.classList.add("is-sel");
      });
    });

    // The list is replaced by the budget card for the flagged tool.
    this.clock.at(4200, () => {
      this.state(2);
      this.showPanel(BUDGET_SIZE, "budget");
    });

    // Onto the fill's leading edge, which is the only draggable part of the
    // track. Aimed at the budget's current position, so it stays correct if that
    // changes.
    this.clock.at(4400, () => this.cursorTo(this.track, FILL_START, 0.5, 1000));

    // The drag: the fill's leading edge moves from below the projected bill to
    // above it and the figure counts up with it. The button stays down
    // throughout.
    this.clock.at(5400, () => {
      this.cursorTarget.classList.add("is-down");
      this.cursorTo(this.track, FILL_END, 0.5, 1200);
      this.fillTarget.style.transition = `width 1200ms ${EASE}`;
      this.setFill(FILL_END);
      this.cancelCount?.();
      this.cancelCount = countUp(
        this.budgetFromValue,
        this.budgetToValue,
        1200,
        (value) => {
          this.amountTarget.textContent = withGroupSpaces(value);
        },
      );
    });

    // Resolved: the track turns green and the new limit is printed.
    this.clock.at(6700, () => {
      this.cursorTarget.classList.remove("is-down");
      this.greenTarget.style.opacity = "1";
      this.cursorTarget.style.opacity = "0";
      this.capTarget.classList.add("is-on");
    });

    this.clock.at(10200, () => {
      this.settle();
      this.clock.at(700, () => this.run());
    });
  }
}
